import type { Vector3 } from 'three';

/**
 * Ghost-replay capture + playback. Fall Line's sim is deterministic and the
 * speedrun loop is the heart of the game, so racing a translucent "ghost" of your
 * own PB is the highest-value retention feature (it's what Momentum Mod et al.
 * lean on). We record the player's feet position on a fixed stride during a run,
 * persist the best one per map, and replay it by sampling/lerping along the
 * stored path against the live run timer — purely visual, no second simulation.
 */

/** Record every Nth tick (~50 Hz) — halves storage with no visible difference. */
export const REPLAY_STRIDE = 2;
/** Cap samples so a stuck/very long run can't blow up localStorage (~120 s). */
const MAX_SAMPLES = 6000;
const KEY_PREFIX = 'fall-line.ghost.';

export interface ReplayData {
  mapId: string;
  /** Finish time in ticks — playback stops here. */
  finishTicks: number;
  /** Flat feet positions [x,y,z, …], one sample every REPLAY_STRIDE ticks. */
  samples: number[];
}

/** Accumulates a run's path; built into a `ReplayData` when the run finishes. */
export class ReplayRecorder {
  private samples: number[] = [];
  private lastTick = -1;

  /** Call each sim tick while the run is live. */
  record(tick: number, pos: Vector3): void {
    if (tick % REPLAY_STRIDE !== 0 || tick === this.lastTick) return;
    if (this.samples.length / 3 >= MAX_SAMPLES) return;
    this.lastTick = tick;
    this.samples.push(pos.x, pos.y, pos.z);
  }

  reset(): void {
    this.samples = [];
    this.lastTick = -1;
  }

  get sampleCount(): number {
    return this.samples.length / 3;
  }

  build(mapId: string, finishTicks: number): ReplayData {
    return { mapId, finishTicks, samples: [...this.samples] };
  }
}

/**
 * Sample the ghost's feet position at `tick` (fractional allowed for render
 * interpolation), writing into `out`. Returns false when the ghost has no data
 * or the run has played past its finish — i.e. the ghost should be hidden.
 */
export function sampleReplay(data: ReplayData, tick: number, out: Vector3): boolean {
  const n = data.samples.length / 3;
  if (n === 0 || tick > data.finishTicks + REPLAY_STRIDE) return false;
  const f = Math.max(0, tick / REPLAY_STRIDE);
  const i = Math.floor(f);
  const a = Math.min(i, n - 1) * 3;
  const b = Math.min(i + 1, n - 1) * 3;
  const t = f - i;
  out.set(
    lerp(data.samples[a], data.samples[b], t),
    lerp(data.samples[a + 1], data.samples[b + 1], t),
    lerp(data.samples[a + 2], data.samples[b + 2], t),
  );
  return true;
}

function lerp(a: number | undefined, b: number | undefined, t: number): number {
  const av = a ?? 0;
  return av + ((b ?? av) - av) * t;
}

export function loadGhost(mapId: string): ReplayData | null {
  try {
    const raw = localStorage.getItem(KEY_PREFIX + mapId);
    if (!raw) return null;
    const data = JSON.parse(raw) as ReplayData;
    if (!Array.isArray(data.samples) || typeof data.finishTicks !== 'number') return null;
    return data;
  } catch {
    return null;
  }
}

export function saveGhost(data: ReplayData): void {
  try {
    localStorage.setItem(KEY_PREFIX + data.mapId, JSON.stringify(data));
  } catch {
    // Storage full / unavailable — the ghost just isn't remembered.
  }
}
